import { Injectable, inject, signal } from '@angular/core';

import { AppConfigService } from '@core/services/infra/app-config.service';

/**
 * Narrow facade of the Google Maps Places element we actually construct. Keeps
 * callers `any`-free without pulling in `@types/google.maps` (which would add
 * thousands of unused symbols to the surface).
 */
export interface GooglePlaceAutocompleteElement extends HTMLElement {
  componentRestrictions?: { country: string[] };
}

export interface GoogleMapsNamespace {
  importLibrary(name: string): Promise<unknown>;
  places: {
    PlaceAutocompleteElement: new (options: { includedRegionCodes: string[] }) => GooglePlaceAutocompleteElement;
    /** Places (New) prediction fetcher — used by `AddressSuggestService` to
     * drive a custom-rendered suggestion list. */
    AutocompleteSuggestion: typeof google.maps.places.AutocompleteSuggestion;
    /** Billing session grouping: N keystroke requests + 1 `fetchFields` are
     * charged as one session when they share a token. */
    AutocompleteSessionToken: typeof google.maps.places.AutocompleteSessionToken;
  };
}

/**
 * CLDR region codes the address autocomplete is restricted to. Places caps
 * `includedRegionCodes` at 15 entries — keep this list at or below that.
 *
 * Shared by `<app-google-address-autocomplete>` (which passes it to the
 * `<gmp-place-autocomplete>` web component) and `AddressSuggestService` (which
 * passes it to `fetchAutocompleteSuggestions`) so both surfaces search the same
 * countries.
 */
export const AUTOCOMPLETE_REGION_CODES: readonly string[] = [
  'be', 'fr', 'nl', 'de', 'lu', 'it', 'es', 'pt',
  'at', 'ch', 'dk', 'no', 'se', 'ie', 'gb'
];

interface GoogleMapsWindow extends Window {
  google?: { maps?: GoogleMapsNamespace };
}

/**
 * Loads the Google Maps JS API (and the `places` library) exactly once for the
 * whole app and hands back the ready `google.maps` namespace.
 *
 * The expensive part of address autocomplete isn't the widget — it's fetching
 * the Maps API key from Secret Manager, downloading the Maps bootstrap script
 * and importing the `places` library. Done lazily when an address field first
 * appears, that chain can take a couple of seconds, during which the user can
 * start typing into a field that isn't actually wired up yet.
 *
 * Calling {@link preload} once at app start runs that chain in the background so
 * the namespace is already resolved by the time any `<app-google-address-
 * autocomplete>` mounts. Components still `await load()`; it just resolves
 * (near-)instantly after a successful preload.
 */
@Injectable({ providedIn: 'root' })
export class GoogleMapsLoaderService {
  private readonly appConfig = inject(AppConfigService);

  private loadPromise: Promise<GoogleMapsNamespace> | null = null;

  /** True once the `places` library has finished importing. */
  readonly ready = signal(false);

  /**
   * Kick off loading in the background without blocking the caller. Safe to call
   * repeatedly — the underlying work runs once. Failures are swallowed here so a
   * cold-start hiccup (offline, key fetch failure) doesn't surface as an
   * unhandled rejection; the lazy {@link load} retries on the next real use.
   */
  preload(): void {
    void this.load().catch(() => {
      /* swallow — a component's later load() will retry */
    });
  }

  /**
   * Resolve with the ready `google.maps` namespace, loading it on first call.
   * The promise is cached; a rejection clears the cache so the next call retries.
   */
  load(): Promise<GoogleMapsNamespace> {
    if (!this.loadPromise) {
      this.loadPromise = this.doLoad().catch(error => {
        // Allow a later call to retry rather than caching the failure forever.
        this.loadPromise = null;
        throw error;
      });
    }
    return this.loadPromise;
  }

  private async doLoad(): Promise<GoogleMapsNamespace> {
    await this.injectLoaderScript();

    const maps = (window as GoogleMapsWindow).google?.maps;
    if (!maps) throw new Error('Google Maps namespace unavailable after loader');

    await maps.importLibrary('places');
    this.ready.set(true);
    return maps;
  }

  private async injectLoaderScript(): Promise<void> {
    const maps = (window as GoogleMapsWindow).google?.maps;
    if (maps?.importLibrary) {
      return;
    }

    const apiKey = await this.appConfig.getMapsApiKey();

    return new Promise<void>((resolve, reject) => {
      const script = document.createElement('script');
      script.innerHTML = `
        (g=>{var h,a,k,p="The Google Maps JavaScript API",c="google",l="importLibrary",q="__ib__",m=document,b=window;b=b[c]||(b[c]={});var d=b.maps||(b.maps={}),r=new Set,e=new URLSearchParams,u=()=>h||(h=new Promise(async(f,n)=>{await (a=m.createElement("script"));e.set("libraries",[...r]+"");for(k in g)e.set(k.replace(/[A-Z]/g,t=>"_"+t[0].toLowerCase()),g[k]);e.set("callback",c+".maps."+q);a.src=\`https://maps.\${c}apis.com/maps/api/js?\`+e;d[q]=f;a.onerror=()=>h=n(Error(p+" could not load."));a.nonce=m.querySelector("script[nonce]")?.nonce||"";m.head.append(a)}));d[l]?console.warn(p+" only loads once. Ignoring:",g):d[l]=(f,...n)=>r.add(f)&&u().then(()=>d[l](f,...n))})
        ({key: "${apiKey}", v: "weekly", loading: "async"});
      `;

      if (!document.head.querySelector('script[src*="maps.googleapis.com"]')) {
        document.head.appendChild(script);
      }

      const checkGoogle = () => {
        const loadedMaps = (window as GoogleMapsWindow).google?.maps;
        if (loadedMaps?.importLibrary) {
          resolve();
        } else {
          setTimeout(checkGoogle, 100);
        }
      };

      checkGoogle();

      setTimeout(() => reject(new Error('Google Maps API loading timeout')), 10000);
    });
  }
}
